import { KpiReadout } from "@/components/ui/kpi-readout";

interface CorrelationPairsData {
  pairs_top10: Array<{ a: string; b: string; pearson: number }>;
}

interface CorrelationSignificanceData {
  fdr_q: number;
  label: string;
  significant: Array<{
    feature: string;
    correlation: number;
    metric_type: string;
    p: number;
    p_bh: number;
  }>;
  non_significant_count: number;
}

type CorrelationsData = CorrelationPairsData | CorrelationSignificanceData;

interface CorrelationsVizProps {
  data: CorrelationsData;
}

function isSignificanceData(data: CorrelationsData): data is CorrelationSignificanceData {
  return "significant" in data;
}

export function CorrelationsViz({ data }: CorrelationsVizProps) {
  if (!data) {
    return <p className="text-xs text-muted">No correlation data available.</p>;
  }

  // Handle correlation_significance kind
  if (isSignificanceData(data)) {
    if (!data.significant || !Array.isArray(data.significant)) {
      return <p className="text-xs text-muted">No significant correlations found.</p>;
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-px rounded-[14px] border border-line bg-line overflow-hidden">
          <KpiReadout label="FDR q-value" value={data.fdr_q.toFixed(3)} />
          <KpiReadout label="Significant" value={data.significant.length.toString()} accent />
          <KpiReadout label="Non-Significant" value={data.non_significant_count.toString()} />
        </div>

        <div>
          <h3 className="mb-3 font-mono text-sm font-semibold text-navy-600 dark:text-zinc-300">
            Statistically Significant Correlations
          </h3>
          <div className="overflow-hidden rounded-lg border border-line">
            <table className="w-full">
              <thead className="bg-surface">
                <tr>
                  <th className="px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                    Feature
                  </th>
                  <th className="px-3 py-2 text-right font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                    Correlation
                  </th>
                  <th className="px-3 py-2 text-right font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                    p-value
                  </th>
                  <th className="px-3 py-2 text-right font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                    p (adj.)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.significant.map((item, idx) => (
                  <tr key={`${item.feature}-${idx}`} className="bg-surface hover:bg-surface-hover">
                    <td className="px-3 py-2 font-mono text-sm text-navy-700 dark:text-zinc-200">
                      {item.feature}
                    </td>
                    <td className="px-3 py-2 font-mono tabular-nums text-sm text-navy-700 dark:text-zinc-200 text-right">
                      {item.correlation.toFixed(3)}
                    </td>
                    <td className="px-3 py-2 font-mono tabular-nums text-sm text-navy-700 dark:text-zinc-200 text-right">
                      {item.p.toExponential(2)}
                    </td>
                    <td className="px-3 py-2 font-mono tabular-nums text-sm text-navy-700 dark:text-zinc-200 text-right">
                      {item.p_bh.toExponential(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Handle correlations kind (pairs_top10)
  const pairsData = data as CorrelationPairsData;
  if (
    !pairsData?.pairs_top10 ||
    !Array.isArray(pairsData.pairs_top10) ||
    pairsData.pairs_top10.length === 0
  ) {
    return <p className="text-xs text-muted">No correlation data available.</p>;
  }

  return (
    <div>
      <h3 className="mb-3 font-mono text-sm font-semibold text-navy-600 dark:text-zinc-300">
        Top Correlations (Pearson r)
      </h3>
      <div className="overflow-hidden rounded-lg border border-line">
        <table className="w-full">
          <thead className="bg-surface">
            <tr>
              <th className="px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                Feature A
              </th>
              <th className="px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                Feature B
              </th>
              <th className="px-3 py-2 text-right font-mono text-[10px] font-semibold uppercase tracking-wider text-navy-300">
                Pearson r
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {pairsData.pairs_top10.map((pair, idx) => (
              <tr key={`${pair.a}-${pair.b}-${idx}`} className="bg-surface hover:bg-surface-hover">
                <td className="px-3 py-2 font-mono text-sm text-navy-700 dark:text-zinc-200">
                  {pair.a}
                </td>
                <td className="px-3 py-2 font-mono text-sm text-navy-700 dark:text-zinc-200">
                  {pair.b}
                </td>
                <td className="px-3 py-2 font-mono tabular-nums text-sm text-navy-700 dark:text-zinc-200 text-right">
                  {pair.pearson.toFixed(3)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
